import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ExternalLink, GraduationCap } from "lucide-react";
import { openOnWeb, webAppUrl } from "@/lib/platform";

/**
 * Shown to students inside the native iOS **and Android** apps in place of
 * any enrollment / payment UI. Both App Store Guideline 3.1.3(b) (iOS) and
 * Google Play Payments policy allow ONE outbound "account management" link
 * per screen that opens the browser to the account/enrollment website —
 * this component IS that link.
 *
 * Copy MUST NOT advertise a lower price on the web or compare against IAP —
 * both stores forbid that (anti-steering rules). Keep it factual: "manage
 * your subscription on the web".
 *
 * Never shown on regular web browsers — only native runtimes render it.
 */
export default function ContinueOnWebCard({
  title = "Manage subscription on our website",
  message = "Sign in to prepmasters.ph with the same email to see payment options and manage your account.",
  path = "/payment-instructions",
  ctaLabel = "Continue on Web",
  compact = false,
  testId = "continue-on-web-card",
}) {
  const domain = (webAppUrl() || "").replace(/^https?:\/\//, "").replace(/\/$/, "");
  const onOpen = () => openOnWeb(path);

  if (compact) {
    return (
      <Card className="p-4 border-2 border-blue-200 bg-blue-50/60 dark:bg-blue-950/20 dark:border-blue-800" data-testid={testId}>
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-blue-600 grid place-items-center shrink-0">
            <ExternalLink className="w-5 h-5 text-white" strokeWidth={2} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-sm text-slate-900 dark:text-slate-100">{title}</div>
            <div className="text-xs text-slate-600 dark:text-slate-300 mt-0.5">{message}</div>
          </div>
        </div>
        <Button
          onClick={onOpen}
          className="w-full mt-3 h-10 bg-blue-600 hover:bg-blue-700 text-white font-semibold"
          data-testid={`${testId}-cta`}
        >
          {ctaLabel}
          <ExternalLink className="w-4 h-4 ml-2" />
        </Button>
      </Card>
    );
  }

  return (
    <Card
      className="p-6 sm:p-8 border-2 border-blue-200 dark:border-blue-800 bg-gradient-to-br from-blue-50 via-indigo-50 to-white dark:from-blue-950/40 dark:via-indigo-950/30 dark:to-slate-900"
      data-testid={testId}
    >
      <div className="flex items-start gap-4">
        <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-600 grid place-items-center shrink-0 shadow-md shadow-blue-200 dark:shadow-none">
          <GraduationCap className="w-7 h-7 text-white" strokeWidth={1.75} />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="font-display text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-50">
            {title}
          </h2>
          <p className="mt-2 text-sm text-slate-700 dark:text-slate-200 leading-relaxed">
            {message}
          </p>
          <p className="mt-3 text-[11px] text-slate-500 dark:text-slate-400">
            You will be taken to <span className="font-semibold">{domain || "our website"}</span> in your browser.
          </p>
          <Button
            onClick={onOpen}
            className="mt-5 h-12 px-6 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold shadow-md shadow-blue-200 dark:shadow-none"
            data-testid={`${testId}-cta`}
          >
            {ctaLabel}
            <ExternalLink className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </Card>
  );
}
